/**
 * WhatsApp Incoming Call Toast — notification for incoming WhatsApp voice calls.
 * Shows caller info with Accept/Reject actions.
 */

import { useState, useEffect } from 'react';
import { Phone, PhoneOff, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { useModuleStore } from '@/stores/module-store';
import type { WhatsAppModule } from '@/modules/tpa/whatsapp/whatsapp.module';

const AUTO_DISMISS_MS = 30_000;

export function WhatsAppIncomingCallToast() {
  const activeCall = useWhatsAppStore((s) => s.activeWhatsAppCall);
  const tpaModule = useModuleStore((s) => s.tpaModule) as WhatsAppModule | null;
  const [accepting, setAccepting] = useState(false);

  // Only show for inbound ringing calls
  const isIncomingRinging =
    activeCall?.direction === 'inbound' && activeCall?.status === 'ringing';

  // Auto-dismiss after timeout
  useEffect(() => {
    if (!isIncomingRinging) return;
    const timer = setTimeout(() => {
      if (activeCall) {
        tpaModule?.rejectIncomingCall(activeCall.callId, activeCall.phoneNumberId);
      }
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [isIncomingRinging, activeCall, tpaModule]);

  if (!isIncomingRinging || !activeCall) return null;

  const handleAccept = async () => {
    if (!tpaModule || accepting) return;
    if (!activeCall.sdpOffer) {
      console.error('[WhatsAppIncomingCall] No SDP offer stored for call', activeCall.callId);
      return;
    }
    setAccepting(true);
    await tpaModule.acceptIncomingCall(
      activeCall.callId,
      activeCall.phoneNumberId,
      activeCall.sdpOffer
    );
    setAccepting(false);
  };

  const handleReject = () => {
    tpaModule?.rejectIncomingCall(activeCall.callId, activeCall.phoneNumberId);
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="rounded-xl border bg-background/95 backdrop-blur-md shadow-lg p-4 w-[300px]">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 animate-pulse">
            <Phone className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Incoming WhatsApp Call</p>
            <p className="text-xs text-muted-foreground truncate">
              {activeCall.contactNumber}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleReject}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/20 py-2 text-sm font-medium transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={accepting}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 py-2 text-sm font-medium transition-colors',
              accepting && 'opacity-70 cursor-not-allowed'
            )}
          >
            {accepting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
